// @flow
import get from 'lodash/get';
import {List, Map} from 'immutable';
import {createSelector, defaultMemoize} from 'reselect';
import {getProcedureOrphanSteps} from 'sweet-graphs';

import * as utils from './utils';
import type {ProcessType} from 'app/process/types';
import {gt} from 'app/utils/i18n';

import type {
  ProcedureStepType,
  ProcedureDescriptionType,
  ProcedureContentType,
} from 'app/tasks/types';
import type {AnyStepType} from 'app/steps/types';
import type {VersionType, ProcedureType, RawEditableContentType} from './types';
import type {StoreShapeType} from 'app/reducers/types';
import type {BasicTeamType} from 'app/teams/types';

export const getProcedureDescription = createSelector<
  {description: string} | void,
  void,
  ProcedureDescriptionType,
  {description: string} | void
>(
  (procedure) => procedure,
  (procedure) =>
    procedure ? utils.getDescription(procedure) : utils.validateStep({})
);

export const getProcedureSteps = createSelector<
  {content: string},
  void,
  Array<ProcedureStepType>,
  {content: string}
>(
  (procedure) => procedure,
  (procedure) => utils.getContent(procedure.content)
);

export const getContentFromProcedure = createSelector<
  ProcedureType | VersionType,
  void,
  ProcedureContentType,
  ProcedureType | VersionType
>(
  (procedure) => procedure,
  (procedure) => ({
    url: procedure.url,
    html_url: procedure.html_url,
    name: procedure.name,
    is_large: Boolean(procedure.is_large),
    description: getProcedureDescription(procedure),
    content: getProcedureSteps(procedure),
    lanes: procedure.lanes,
  })
);

const getProcedureStepsWithOrphans = createSelector<
  {content: string},
  void,
  Array<ProcedureStepType>,
  {content: string}
>(
  (procedure) => procedure,
  (procedure) => utils.getContentWithOrphans(procedure.content)
);

export const getContentFromProcedureWithoutStepCleanup = createSelector<
  RawEditableContentType,
  void,
  ProcedureContentType,
  RawEditableContentType
>(
  (procedure) => procedure,
  (procedure) => ({
    url: procedure.url,
    html_url: procedure.html_url,
    name: procedure.name,
    is_large: Boolean(procedure.is_large),
    lanes: procedure.lanes,
    description: getProcedureDescription(procedure),
    content: getProcedureStepsWithOrphans(procedure),
  })
);

export const currVersionTooltipTextSelector = createSelector<
  {approved_at: null | string},
  void,
  string,
  {approved_at: null | string}
>(
  (procedure) => procedure,
  (procedure) =>
    get(procedure, 'approved_at') ? gt('Live') : gt('Most recent draft')
);

const matchProcessByHashid = (processes, hashid) =>
  processes != null
    ? processes.find((process) => process.get('hashid') === hashid)
    : undefined;

export const getProcessFromParams = createSelector<
  StoreShapeType,
  {
    +location: {+query: {+gid?: string}},
    procedureId: number,
  },
  ProcessType | void,
  string | void,
  List<Map<string, any>> | void,
  Map<string, Map<string, any>> | void
>(
  (state, props) => props.location.query.gid,
  (state, props) =>
    state.proceduresPage.getIn([
      'byId',
      (props.procedureId || 0).toString(),
      'processes',
    ]),
  (state) => state.processArea.getIn(['processes', 'byId']),
  (hashid, processesFromProcedure, processes) => {
    if (hashid != null) {
      let process = matchProcessByHashid(processes, hashid);
      if (process == null) {
        process = matchProcessByHashid(processesFromProcedure, hashid);
      }
      // $FlowFixMe
      return process != null ? process.toJS() : process;
    }
  }
);

export const denormalizeDocument = (
  basicTeams: any,
  document: ProcedureType | ProcessType
) => {
  return {
    ...document,
    team_names: document.team_names.map<BasicTeamType>(({id}) =>
      basicTeams.get(id.toString(), Map({})).toJS()
    ),
  };
};

export const denormalizeProcedureFactory =
  (state: StoreShapeType) => (procedure: ProcedureType) =>
    denormalizeDocument(state.teamsArea.get('basicTeams'), procedure);

export const getMemoizedProcedureOrphanSteps = defaultMemoize(
  (steps: Array<ProcedureStepType>, stepsBlockedAt: string | null | boolean) =>
    getProcedureOrphanSteps(steps, stepsBlockedAt),
  // this equality check function is called with all inputs in turn.
  (
    prev: Array<AnyStepType> | string | null,
    current: Array<AnyStepType> | string | null
  ) =>
    Array.isArray(prev) && Array.isArray(current)
      ? utils.procedureStructureIsEqual(prev, current)
      : prev === current
);
